/* ============================================================================
 * File: frontend/src/components/dashboard/QuickActionsWidget.js
 * Version: 134.0
 * Date: 2025-12-12 14:40:00 EST
 * Author: Dashboard Team
 * Description: Enhanced Quick Actions with FIXED GRID LAYOUT.
 *              - All cards in same row have EQUAL HEIGHT
 *              - AI Reputation (2x) and Calendar (2x) in top row
 *              - Manage Leads (2x) and User Mgmt (2x) in bottom row
 *              - Proper grid-auto-rows for consistent heights
 * 
 * Changelog:
 * v134.0 (2025-12-12) - 🔧 GRID FIX
 *                     - Fixed: All cards now stretch to same height in each row
 *                     - Changed grid to 2 rows with proper auto-rows
 *                     - Removed minHeight, using grid-auto-rows instead
 * v132.0 (2025-12-12) - Major redesign with location scores
 * ============================================================================ */

import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Zap, Users, FileText, Briefcase, Calendar, Shield, MessageCircle, Upload, Target, Video, VideoOff, Clock } from 'lucide-react';

// ── ПАЛИТРА ЭКРАНА «СЕГОДНЯ» → §3.1 (Ф1, quick-260909-jl8) ─────────────────
// Откат в каждой строке — ПРЕЖНИЙ литерал ЭТОГО ЖЕ места. На fitenko токен
// объявлен (styles/fitenko-tokens.css, селектор html.fitenko) и берётся он;
// на MLG и ELN токена НЕТ — берётся откат, то есть РОВНО прежний цвет.
// 🔴 Значения отката менять нельзя: ими доказывается неизменность чужих арендаторов.
// 🔴 Голый var(--ink) запрещён: у MLG он разрешается в ПУСТОТУ (отсутствие цвета),
//    а не в другой цвет — объявление становится недействительным.
// Класс C (подложки чипов) — по решению владельца от 2026-09-09, вариант B:
// подложка выводится правилом color-mix(in srgb, АКЦЕНТ 14%, --surface); правило
// объявлено ОДИН раз в pages/Dashboard.css под селектором html.fitenko. Бирюза —
// вариант T1: своего тона в §3.1 нет, берётся --green-accent.
const P = {
    ink:        'var(--ink, #1e293b)',  // класс A
    line:       'var(--line, #e2e8f0)',  // класс A
    muted:      'var(--ink-muted, #64748b)',  // класс A
    mutedSoft:  'var(--ink-muted, #94a3b8)',  // класс A
    surface:    'var(--surface, #ffffff)',  // класс A
    amber10:    'var(--amber-accent, #fde047)',  // класс B
    amber2:     'var(--amber-accent, #eab308)',  // класс B
    amber3:     'var(--amber-accent, #fbbf24)',  // класс B
    amber4:     'var(--amber-accent, #d97706)',  // класс B
    amber5:     'var(--amber-accent, #ca8a04)',  // класс B
    amber9:     'var(--amber-accent, #713f12)',  // класс B
    blue1:      'var(--blue-accent, #3b82f6)',  // класс B
    blue2:      'var(--blue-accent, #2563eb)',  // класс B
    blue4:      'var(--blue-accent, #1e40af)',  // класс B
    blue5:      'var(--blue-accent, #60a5fa)',  // класс B
    green4:     'var(--green-accent, #6ee7b7)',  // класс B
    red10:      'var(--red-accent, #fda4af)',  // класс B
    red11:      'var(--red-accent, #881337)',  // класс B
    red12:      'var(--red-accent, #db2777)',  // класс B
    red13:      'var(--red-accent, #f9a8d4)',  // класс B
    red4:       'var(--red-accent, #e11d48)',  // класс B
    red8:       'var(--red-accent, #831843)',  // класс B
    red9:       'var(--red-accent, #be185d)',  // класс B
    teal2:      'var(--green-accent, #059669)',  // класс B
    teal5:      'var(--green-accent, #065f46)',  // класс B
    chipAmber2: 'var(--chip-amber-bg, #fef9c3)',  // класс C
    chipBlue1:  'var(--chip-blue-bg, #eff6ff)',  // класс C
    chipGreen3: 'var(--chip-green-bg, #ecfdf5)',  // класс C
    chipRed3:   'var(--chip-red-bg, #fce7f3)',  // класс C
    chipRed4:   'var(--chip-red-bg, #fff1f2)',  // класс C
};




const ICON_MAP = {
    'users': Users, 'file-text': FileText, 'briefcase': Briefcase, 'calendar': Calendar, 'shield': Shield,
    'message-circle': MessageCircle, 'upload': Upload, 'target': Target, 'video': Video, 'video-off': VideoOff,
    'clock': Clock, 'default': Zap
};

const QuickActionsWidget = ({ actions, onActionClick }) => {
    const navigate = useNavigate();
    if (!actions || actions.length === 0) return null;

    const handleCardClick = (action) => {
        if (action.link && action.link.startsWith('#')) {
            if (onActionClick) {
                onActionClick(action.link);
            }
        } else {
            navigate(action.link);
        }
    };

    return (
        <div className="card" style={{
            fontFamily: '-apple-system, system-ui, sans-serif',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <div className="card-header" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '16px',
                justifyContent: 'flex-start'
            }}>
                <Zap size={18} style={{ stroke: P.amber2, fill: P.amber2 }} />
                <h3 style={{
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: P.ink,
                    margin: 0
                }}>Quick Actions</h3>
            </div>

            {/* 🎯 FIXED GRID: 2 rows, each card spans 2 columns */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',  // 4 columns total
                gridTemplateRows: 'repeat(2, 1fr)',     // 2 equal rows
                gap: '16px',
                flex: 1,
                minHeight: '300px'  // Ensure enough space for 2 rows
            }}>
                {actions.map((action, idx) => {
                    const IconComponent = ICON_MAP[action.icon] || ICON_MAP.default;

                    // ========================================
                    // ALL CARDS ARE 2x WIDTH (span 2 columns)
                    // ========================================
                    const gridColumn = 'span 2';

                    // ========================================
                    // THEME ENGINE
                    // ========================================
                    let bg = P.surface, border = `1px solid ${P.line}`, textMain = P.ink, textFoot = P.amber4, iconCol = P.muted;

                    // AI Reputation - Dark theme
                    if (action.key === 'ai_reputation') {
                        bg = P.ink;
                        border = 'none';
                        textMain = P.surface;
                        textFoot = P.mutedSoft;
                        iconCol = P.amber3;
                    }
                    // Calendar - Mint green theme with status colors
                    else if (action.key === 'calendar') {
                        bg = P.chipGreen3;
                        border = `1px solid ${P.green4}`;
                        textMain = P.teal5;
                        textFoot = P.amber4;
                        iconCol = P.teal2;

                        // Status-based colors
                        if (action.status === 'warning') {
                            bg = P.chipAmber2;
                            border = `1px solid ${P.amber10}`;
                            textMain = P.amber9;
                            iconCol = P.amber5;
                        } else if (action.status === 'critical') {
                            bg = P.chipRed3;
                            border = `1px solid ${P.red13}`;
                            textMain = P.red8;
                            iconCol = P.red9;
                        }
                    }
                    // SPECIAL: Embed Widget - Blue Theme
                    else if (action.key === 'embed_widget') {
                        bg = P.chipBlue1; // blue-50
                        border = `1px solid ${P.blue5}`; // blue-400
                        textMain = P.blue4; // blue-800
                        textFoot = P.blue1; // blue-500
                        iconCol = P.blue2; // blue-600
                    }
                    // SPECIAL: Live Monitor - Red/Rose Theme
                    else if (action.key === 'live_monitor') {
                        bg = P.chipRed4; // rose-50
                        border = `1px solid ${P.red10}`; // rose-300
                        textMain = P.red11; // rose-900
                        textFoot = P.red4; // rose-600
                        iconCol = P.red12; // pink-600
                    }

                    const mainDisplay = action.main_text || action.title;
                    const footerDisplay = action.footer_text || "";

                    return (
                        <div key={idx}
                            onClick={() => handleCardClick(action)}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                padding: '20px',
                                borderRadius: '16px',
                                background: bg,
                                border: border,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                gridColumn: gridColumn,
                                // 🎯 KEY FIX: Remove height: '100%' and minHeight
                                // Let grid control the height
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.02)';
                            }}
                        >
                            {/* ========================================
                                CARD CONTENT
                            ======================================== */}

                            {/* Header */}
                            <div>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '12px'
                                }}>
                                    <IconComponent size={22} color={iconCol} />
                                    <span style={{
                                        fontSize: '0.9rem',
                                        fontWeight: '700',
                                        color: textMain,
                                        opacity: 0.8
                                    }}>
                                        {action.title}
                                    </span>
                                </div>

                                {/* Main Text */}
                                <div style={{
                                    fontSize: '1.2rem',
                                    fontWeight: '800',
                                    color: textMain,
                                    lineHeight: '1.2',
                                    letterSpacing: '-0.5px'
                                }}>
                                    {mainDisplay}

                                    {/* Calendar: Show online count inline */}
                                    {action.key === 'calendar' && action.online_count > 0 && (
                                        <span style={{
                                            marginLeft: '12px',
                                            fontSize: '1rem',
                                            fontWeight: '800',
                                            color: P.amber4,
                                            textTransform: 'uppercase'
                                        }}>
                                            {action.online_count} ONLINE
                                        </span>
                                    )}
                                </div>

                                {/* ========================================
                                    AI REPUTATION: LOCATION SCORES
                                ======================================== */}
                                {action.key === 'ai_reputation' && (action.best_location || action.worst_location) && (
                                    <div style={{
                                        marginTop: '16px',
                                        fontSize: '0.85rem',
                                        lineHeight: '1.6'
                                    }}>
                                        {/* Best Location */}
                                        {action.best_location && (
                                            <div style={{ marginBottom: '8px' }}>
                                                <span style={{
                                                    color: P.amber3,
                                                    fontWeight: '800',
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.7rem',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    BEST LOCATION
                                                </span>
                                                <div style={{
                                                    color: P.surface,
                                                    fontWeight: '700',
                                                    marginTop: '4px'
                                                }}>
                                                    {action.best_location} Score {action.best_score}
                                                </div>
                                            </div>
                                        )}

                                        {/* Worst Location */}
                                        {action.worst_location && (
                                            <div>
                                                <span style={{
                                                    color: P.amber3,
                                                    fontWeight: '800',
                                                    textTransform: 'uppercase',
                                                    fontSize: '0.7rem',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    ISSUES LOCATION
                                                </span>
                                                <div style={{
                                                    color: P.surface,
                                                    fontWeight: '700',
                                                    marginTop: '4px'
                                                }}>
                                                    {action.worst_location} Score {action.worst_score}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Footer (Orange uppercase) */}
                            {footerDisplay && (
                                <div style={{
                                    marginTop: '16px',
                                    fontSize: '0.65rem',
                                    fontWeight: '800',
                                    color: textFoot,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    {footerDisplay}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

QuickActionsWidget.propTypes = { actions: PropTypes.array.isRequired };
export default QuickActionsWidget;